/**
 * The audio engine. Every sound in the game is synthesised here at runtime with the
 * Web Audio API: no audio files to download, and every hit sounds a little different.
 *
 *   sources -> per-sound gain/pan -> bus (sfx | ambient | music) -> master -> compressor -> speakers
 *                                 \-> reverb send (shared convolver)
 */
export type Bus = 'sfx' | 'ambient' | 'music';
export interface AudioSettings { master: number; sfx: number; ambient: number; music: number; muted: boolean }

const KEY = 'hollowmere-audio';
const DEFAULTS: AudioSettings = { master: 0.85, sfx: 0.8, ambient: 0.6, music: 0.45, muted: false };

export interface Voice { gain?: number; pan?: number; bus?: Bus; reverb?: number }
export interface ToneOpts extends Voice { type?: OscillatorType; freq: number; to?: number; at?: number; dur: number; attack?: number; detune?: number; filter?: { type: BiquadFilterType; freq: number; q?: number } }
export interface NoiseOpts extends Voice { color?: 'white' | 'pink' | 'brown'; at?: number; dur: number; attack?: number; filter: BiquadFilterType; freq: number; to?: number; q?: number }

export class AudioEngine {
  ctx: AudioContext | null = null;
  settings: AudioSettings;
  private master!: GainNode;
  private buses!: Record<Bus, GainNode>;
  private reverbIn!: GainNode;
  private duckGain!: GainNode;
  private noise: Record<string, AudioBuffer> = {};
  private voices = 0;
  private starters: (() => void)[] = [];

  constructor() {
    let saved: Partial<AudioSettings> = {};
    try { saved = JSON.parse(localStorage.getItem(KEY) ?? '{}'); } catch { /* no storage */ }
    this.settings = { ...DEFAULTS, ...saved };
  }

  /** Browsers only allow audio after a click or key press; call this from one. */
  unlock() {
    if (this.ctx) { if (this.ctx.state === 'suspended') void this.ctx.resume(); return; }
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx({ latencyHint: 'interactive' });
    this.ctx = ctx;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -16; comp.knee.value = 12; comp.ratio.value = 4; comp.attack.value = 0.004; comp.release.value = 0.2;
    comp.connect(ctx.destination);
    this.master = ctx.createGain(); this.master.connect(comp);
    this.duckGain = ctx.createGain(); this.duckGain.connect(this.master);
    this.buses = { sfx: ctx.createGain(), ambient: ctx.createGain(), music: ctx.createGain() };
    this.buses.sfx.connect(this.master); this.buses.ambient.connect(this.master); this.buses.music.connect(this.duckGain);
    const verb = ctx.createConvolver();
    verb.buffer = this.impulse(2.4, 2.6);
    this.reverbIn = ctx.createGain(); this.reverbIn.gain.value = 0.9;
    this.reverbIn.connect(verb); verb.connect(this.master);
    for (const c of ['white', 'pink', 'brown'] as const) this.noise[c] = this.makeNoise(c, 3);
    this.apply();
    for (const s of this.starters) s();
  }
  onStart(fn: () => void) { if (this.ctx) fn(); else this.starters.push(fn); }

  apply() {
    try { localStorage.setItem(KEY, JSON.stringify(this.settings)); } catch { /* ignore */ }
    if (!this.ctx) return;
    const s = this.settings, t = this.ctx.currentTime;
    this.master.gain.setTargetAtTime(s.muted ? 0 : s.master, t, 0.05);
    this.buses.sfx.gain.setTargetAtTime(s.sfx, t, 0.05);
    this.buses.ambient.gain.setTargetAtTime(s.ambient, t, 0.05);
    this.buses.music.gain.setTargetAtTime(s.music, t, 0.05);
  }

  get now() { return this.ctx?.currentTime ?? 0; }
  bus(name: Bus) { return this.buses[name]; }
  noiseBuffer(c: 'white' | 'pink' | 'brown') { return this.noise[c]; }

  /** Lower the music for a moment (level-ups, discoveries). */
  duck(amount = 0.35, seconds = 2.5) {
    if (!this.ctx) return;
    const g = this.duckGain.gain, t = this.ctx.currentTime;
    g.cancelScheduledValues(t); g.setValueAtTime(g.value, t);
    g.linearRampToValueAtTime(amount, t + 0.15); g.setValueAtTime(amount, t + seconds); g.linearRampToValueAtTime(1, t + seconds + 1.5);
  }

  /** gain -> stereo pan -> bus, plus an optional reverb send. Returns the input node. */
  out(v: Voice, dur: number): GainNode | null {
    const ctx = this.ctx;
    if (!ctx || this.voices > 64) return null;
    const g = ctx.createGain(); g.gain.value = v.gain ?? 1;
    let tail: AudioNode = g;
    if (v.pan) { const p = ctx.createStereoPanner(); p.pan.value = Math.max(-1, Math.min(1, v.pan)); g.connect(p); tail = p; }
    tail.connect(this.buses[v.bus ?? 'sfx']);
    if (v.reverb) { const s = ctx.createGain(); s.gain.value = v.reverb; tail.connect(s); s.connect(this.reverbIn); }
    this.voices++;
    setTimeout(() => { this.voices--; try { g.disconnect(); } catch { /* gone */ } }, (dur + 0.5) * 1000);
    return g;
  }

  /** An oscillator with a fast-attack, exponential-decay envelope and optional pitch glide. */
  tone(o: ToneOpts) {
    const ctx = this.ctx; if (!ctx) return;
    const t = ctx.currentTime + (o.at ?? 0), dest = this.out(o, (o.at ?? 0) + o.dur); if (!dest) return;
    const osc = ctx.createOscillator(), env = ctx.createGain();
    osc.type = o.type ?? 'sine'; osc.frequency.setValueAtTime(o.freq, t);
    if (o.to) osc.frequency.exponentialRampToValueAtTime(Math.max(1, o.to), t + o.dur);
    if (o.detune) osc.detune.value = o.detune;
    const a = o.attack ?? 0.005;
    env.gain.setValueAtTime(0.0001, t); env.gain.exponentialRampToValueAtTime(1, t + a); env.gain.exponentialRampToValueAtTime(0.0001, t + o.dur);
    let node: AudioNode = osc;
    if (o.filter) { const f = ctx.createBiquadFilter(); f.type = o.filter.type; f.frequency.value = o.filter.freq; f.Q.value = o.filter.q ?? 0.7; osc.connect(f); node = f; }
    node.connect(env); env.connect(dest);
    osc.start(t); osc.stop(t + o.dur + 0.05);
  }

  /** Filtered noise with an envelope; the workhorse for chops, splashes, footsteps and fire. */
  noiseHit(o: NoiseOpts) {
    const ctx = this.ctx; if (!ctx) return;
    const t = ctx.currentTime + (o.at ?? 0), dest = this.out(o, (o.at ?? 0) + o.dur); if (!dest) return;
    const src = ctx.createBufferSource(); src.buffer = this.noise[o.color ?? 'white'];
    const f = ctx.createBiquadFilter(); f.type = o.filter; f.frequency.setValueAtTime(o.freq, t); f.Q.value = o.q ?? 0.8;
    if (o.to) f.frequency.exponentialRampToValueAtTime(Math.max(20, o.to), t + o.dur);
    const env = ctx.createGain(), a = o.attack ?? 0.003;
    env.gain.setValueAtTime(0.0001, t); env.gain.exponentialRampToValueAtTime(1, t + a); env.gain.exponentialRampToValueAtTime(0.0001, t + o.dur);
    src.connect(f); f.connect(env); env.connect(dest);
    src.start(t, Math.random() * 2); src.stop(t + o.dur + 0.05);
  }

  /** Several inharmonic partials: bells, anvils, ore clinks. */
  bell(freq: number, partials: [number, number][], dur: number, v: Voice & { at?: number }) {
    for (const [ratio, amp] of partials) this.tone({ ...v, freq: freq * ratio, dur: dur * (1.2 - Math.min(0.9, ratio * 0.08)), gain: (v.gain ?? 1) * amp, attack: 0.002 });
  }

  private makeNoise(color: 'white' | 'pink' | 'brown', seconds: number) {
    const ctx = this.ctx!, n = ctx.sampleRate * seconds, buf = ctx.createBuffer(1, n, ctx.sampleRate), d = buf.getChannelData(0);
    let b0 = 0, b1 = 0, b2 = 0, last = 0;
    for (let i = 0; i < n; i++) {
      const w = Math.random() * 2 - 1;
      if (color === 'white') d[i] = w * 0.5;
      else if (color === 'pink') { b0 = 0.99765 * b0 + w * 0.099046; b1 = 0.963 * b1 + w * 0.2965164; b2 = 0.57 * b2 + w * 1.0526913; d[i] = (b0 + b1 + b2 + w * 0.1848) * 0.11; }
      else { last = (last + 0.02 * w) / 1.02; d[i] = last * 3.2; }
    }
    return buf;
  }
  private impulse(seconds: number, decay: number) {
    const ctx = this.ctx!, n = Math.floor(ctx.sampleRate * seconds), buf = ctx.createBuffer(2, n, ctx.sampleRate);
    for (let c = 0; c < 2; c++) { const d = buf.getChannelData(c); for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / n, decay) * 0.6; }
    return buf;
  }
}

export const audio = new AudioEngine();
export const rand = (a: number, b: number) => a + Math.random() * (b - a);
export const mtof = (m: number) => 440 * Math.pow(2, (m - 69) / 12);
